import { MutableRefObject, useCallback, useEffect, useState } from 'react';
import { getIsRtl } from '../locale/i18n';

type Props = {
  ref: MutableRefObject<HTMLDivElement>;
  onEndReached?: () => void;
  endThreshold?: number;
};
export const useCarouselScroll = ({ ref, onEndReached, endThreshold = 0 }: Props) => {
  const isRtl = getIsRtl();

  const [edge, setEdge] = useState<'left' | 'right' | null>(!isRtl ? 'left' : 'right');

  const handleScrollLeft = useCallback(() => {
    const { scrollLeft, clientWidth } = ref.current;

    ref.current.scrollTo({
      left: scrollLeft - clientWidth,
      behavior: 'smooth',
    });
  }, [ref]);

  const handleScrollRight = useCallback(() => {
    const { scrollLeft, clientWidth } = ref.current;

    ref.current.scrollTo({
      left: scrollLeft + clientWidth,
      behavior: 'smooth',
    });
  }, [ref]);

  const updateEdgeState = useCallback(() => {
    const { scrollLeft, scrollWidth, clientWidth } = ref.current;

    const isLeftEdge = !isRtl ? scrollLeft <= 0 : Math.abs(scrollLeft) + clientWidth >= scrollWidth;
    if (isLeftEdge) {
      setEdge('left');
      return;
    }

    const isRightEdge = !isRtl ? scrollLeft + clientWidth >= scrollWidth : scrollLeft >= 0;
    if (isRightEdge) {
      setEdge('right');
      return;
    }

    setEdge(null);
  }, [isRtl, ref]);

  const handleScroll = useCallback(() => {
    if (!ref.current) {
      return;
    }

    const { scrollLeft, scrollWidth, clientWidth } = ref.current;

    const adjustedScrollLeft = Math.abs(scrollLeft);
    if (scrollWidth - adjustedScrollLeft - clientWidth < endThreshold) {
      onEndReached?.();
    }

    updateEdgeState();
  }, [endThreshold, onEndReached, ref, updateEdgeState]);

  const [hasScroll, setHasScroll] = useState(false);

  useEffect(() => {
    if (!ref.current) {
      setHasScroll(false);
      return;
    }

    const { scrollWidth, clientWidth } = ref.current;
    setHasScroll(clientWidth < scrollWidth);
  }, [ref]);

  return {
    handleScroll,
    handleScrollLeft,
    handleScrollRight,
    edge,
    hasScroll,
  };
};
